import {
  ReportDashboardComponent,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewMode,
  ReportDashboardMetricOptions,
} from '@core/reports-types';

import { isNetChangeOverTime } from './net-change';

export const determineAvailableViewModes = (
  x: Partial<Pick<ReportDashboardComponent, 'primaryGroup' | 'secondaryGroup'>>,
  metric: ReportDashboardMetricOptions,
): Record<ReportDashboardComponentViewMode, boolean> => {
  const primaryGroup = x.primaryGroup ?? '';
  const secondaryGroup = x.secondaryGroup ?? '';
  switch (metric.kind) {
    case ReportDashboardComponentKind.Breakdown:
      return {
        [ReportDashboardComponentViewMode.Chart]: primaryGroup !== '',
        [ReportDashboardComponentViewMode.Table]: primaryGroup !== '',
        [ReportDashboardComponentViewMode.Summary]: primaryGroup === '' || secondaryGroup === '',
      };

    case ReportDashboardComponentKind.Trend:
      return {
        [ReportDashboardComponentViewMode.Chart]: true,
        [ReportDashboardComponentViewMode.Table]: true,
        [ReportDashboardComponentViewMode.Summary]: !(primaryGroup !== ''),
      };
    case ReportDashboardComponentKind.SingleMetric:
      return {
        [ReportDashboardComponentViewMode.Chart]:
          metric.defaultGroup !== null || primaryGroup !== '',
        [ReportDashboardComponentViewMode.Table]:
          metric.defaultGroup !== null || primaryGroup !== '',
        [ReportDashboardComponentViewMode.Summary]: (() => {
          // TODO remove after #86c5d6zvc
          if (metric.metric === 'total_salary' || metric.metric === 'average_salary') {
            return primaryGroup === '' && secondaryGroup === '';
          }
          return true;
        })(),
      };

    case ReportDashboardComponentKind.Funnel:
      return {
        [ReportDashboardComponentViewMode.Chart]: true,
        [ReportDashboardComponentViewMode.Summary]: true,
        [ReportDashboardComponentViewMode.Table]: true,
      };

    case ReportDashboardComponentKind.TimeBased:
      return {
        [ReportDashboardComponentViewMode.Chart]: primaryGroup !== '',
        [ReportDashboardComponentViewMode.Table]: primaryGroup !== '',
        [ReportDashboardComponentViewMode.Summary]: true,
      };

    // Over time the rows are periods, so there is always something to chart or
    // tabulate. Split by a dimension it behaves like a breakdown: ungrouped it
    // collapses to a single row, which only makes sense as a summary tile.
    case ReportDashboardComponentKind.NetChange:
      return {
        [ReportDashboardComponentViewMode.Chart]:
          isNetChangeOverTime(metric) || primaryGroup !== '',
        [ReportDashboardComponentViewMode.Table]:
          isNetChangeOverTime(metric) || primaryGroup !== '',
        [ReportDashboardComponentViewMode.Summary]: true,
      };

    default:
      return {
        [ReportDashboardComponentViewMode.Chart]: false,
        [ReportDashboardComponentViewMode.Summary]: false,
        [ReportDashboardComponentViewMode.Table]: true,
      };
  }
};

export const isViewModeAvailable = (
  metric: ReportDashboardMetricOptions,
  viewMode: ReportDashboardComponentViewMode,
  component: { primaryGroup?: string | null; secondaryGroup?: string | null },
): boolean => determineAvailableViewModes(component, metric)[viewMode] ?? false;
